/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, Spinner, H2, Switch, Button } from '@increasecard/typed-components';
import { useCountry, Country, useEventTracker } from '@increasecard/increase-core';
import { CredRow, Establishment, INITIAL_CREDROW, Shop } from '@card/types';
import { CredentialShops, useModal, CountryFilter, Constants } from '@card/commonlib';

import { useShallowEqualSelector } from '../../utils/reduxUtils';
import Paginator from '../../common/components/Paginator';
import { EstablishmentsActions } from '../settings/actions/establishmentsActions';
import { CredentialsActions } from './actions';
import { ConfirmAutoRenewalModal } from './components/ConfirmAutoRenewalModal';
import CredentialModal from './CredentialModal';
import DeleteCredentialModal from './components/DeleteCredentialModal';
import { AutorenewAllToggle } from '../settings/components/AutorenewAllToggle';
import { useLocation } from 'react-router-dom';

export function CredentialsSettings() {
  const country = useCountry() as Country;
  const tracker = useEventTracker();
  const autoRenewalModal = useModal();
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [showDeleteCredentialModal, setShowDeleteCredentialModal] = useState(false);
  const [credentialToEdit, setCredentialToEdit] = useState(INITIAL_CREDROW);
  const [credentialToDelete, setCredentialToDelete] = useState('');
  const actionParam = new URLSearchParams(useLocation().search).get('action');
  const [showOnlyBlocked, setShowOnlyBlocked] = useState(actionParam === 'blocked_credentials');

  /* Handle query string changes (clicks on the global blocked credentials alert when this
   * component is mounted)
   */
  useEffect(() => {
    setShowOnlyBlocked(actionParam === 'blocked_credentials');
  }, [actionParam]);

  const onFilterConflictsChange = () => {
    setShowOnlyBlocked((prevValue) => !prevValue);
  };

  const toggleCredentialModal = () => {
    setShowCredentialModal(!showCredentialModal);
  };

  const newCredential = () => {
    setCredentialToEdit(INITIAL_CREDROW);
    toggleCredentialModal();
  };

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { credentials, shops } = useShallowEqualSelector((state) => ({
    credentials: state.credentials,
    shops: state.shops
  }));

  const loadPage = useCallback(
    (page: number) => {
      if (showOnlyBlocked) {
        dispatch(CredentialsActions.fetchBlocked({ page }));
      } else {
        dispatch(CredentialsActions.fetch({ page }));
      }
    },
    [dispatch, showOnlyBlocked]
  );

  useEffect(() => {
    // load first page on init
    loadPage(1);
    dispatch(CredentialsActions.getMercadopagoLink());
  }, [loadPage, dispatch]);

  const toggleDeleteCredentialModal = () => {
    setShowDeleteCredentialModal(!showDeleteCredentialModal);
  };

  const editCredential = (credential: CredRow) => {
    setCredentialToEdit(credential);
    dispatch(CredentialsActions.setStatus('EDITABLE'));
    toggleCredentialModal();
  };

  const deleteCredential = () => {
    tracker.track('Delete credential', { credential_id: credentialToDelete });
    dispatch(CredentialsActions.deleteCredential(credentialToDelete));
    toggleDeleteCredentialModal();
  };

  const toggleAutoRenewal = (activate: boolean, credentialId: string) => {
    tracker.track(`Credential Automatic renewal ${activate ? 'on' : 'off'}`, {
      credential_id: credentialId
    });
    if (activate) {
      dispatch(CredentialsActions.enableAutoRenewal(credentialId));
      dispatch(CredentialsActions.toggleAutomaticRenewal(credentialId, true));
    } else {
      dispatch(CredentialsActions.toggleAutomaticRenewal(credentialId, false));
      dispatch(CredentialsActions.disableAutoRenewal(credentialId));
    }
  };

  const onAutoRenewalModalClose = () => {
    autoRenewalModal.close();
  };

  const onAutoRenewalModalConfirm = () => {
    if (autoRenewalModal.data !== null) {
      const { automatic_renewal, credential_id } = autoRenewalModal.data;
      toggleAutoRenewal(!automatic_renewal, credential_id);
    }
    autoRenewalModal.close();
  };

  return (
    <React.Fragment>
      <H2 style={{ marginBottom: '24px' }}>{t(`abm:Credentials:title`)}</H2>
      <Grid style={{ marginBottom: '16px', alignItems: 'center' }}>
        <div className="grid-span-desktop-2" style={{ maxWidth: '100%' }}>
          <CountryFilter hideFor={[Constants.Countries.URUGUAY]}>
            <AutorenewAllToggle />
          </CountryFilter>
        </div>
        <div className="grid-span-desktop-2">
          <Switch
            checked={showOnlyBlocked}
            id="switchConflictCredentials"
            label={t(`abm:Credentials:filterConflicts`)}
            onChange={onFilterConflictsChange}
            size="small"
          />
        </div>
        <div className="grid-span-desktop-6 grid-start-desktop-7" style={{ textAlign: 'right' }}>
          <Button buttonType="invisible" onClick={newCredential}>
            {t(`abm:Shops:addCredential`)}
          </Button>
        </div>
      </Grid>
      <Grid rowGap={2}>
        {credentials.fetching && <Spinner size={24} />}
        {credentials.credRows &&
          credentials.credRows.map((credential, index) => {
            const toggleEstablishment = (establishmentId: string, assign: boolean) => {
              const shopId = assign ? shops.shopsList[0].id : '';
              dispatch(
                EstablishmentsActions.assignShop({
                  establishmentId: establishmentId,
                  shopId: shopId,
                  credentialId: credential.credential_id
                })
              );
            };

            const selectShop = (shopSelected: Shop, establishmentId: string) => {
              dispatch(
                EstablishmentsActions.assignShop({
                  establishmentId: establishmentId,
                  shopId: shopSelected.id,
                  credentialId: credential.credential_id
                })
              );
            };

            const onEditCredential = () => {
              editCredential(credential);
            };

            const toggleAllEstablishments = (assign: boolean) => {
              /* Básicamente, voy armando objetos de tipo { establishmentId, shopId} SOLO de los objetos */
              /* que hay que modificar, con el objetivo de hacerlo lo más óptimo posible */

              type assignationToSendType = {
                assignations: {
                  establishmentId: string;
                  shopId: string;
                }[];
                credentialId: string;
                assignAll: boolean;
              };

              const assignationToSend: assignationToSendType = {
                assignations: [],
                credentialId: credential.credential_id,
                assignAll: assign
              };
              credential.establishments.forEach((establishment: Establishment) => {
                const assignation: {
                  establishmentId: string;
                  shopId: string;
                } = {
                  establishmentId: establishment.id,
                  shopId: ''
                };
                let edited = false;
                if (assign && !establishment.shop_id) {
                  assignation.shopId = shops.shopsList[0].id;
                  edited = true;
                }
                if (!assign && establishment.shop_id) {
                  assignation.shopId = '';
                  edited = true;
                }
                if (edited) assignationToSend.assignations.push(assignation);
              });
              if (assignationToSend.assignations.length > 0)
                dispatch(EstablishmentsActions.assignAllEstablishments(assignationToSend));
            };

            const onToggleAutomaticRenewal = () => {
              if (!credential.automatic_renewal) {
                autoRenewalModal.open(credential);
              } else {
                toggleAutoRenewal(!credential.automatic_renewal, credential.credential_id);
              }
            };

            const onDeleteCredential = () => {
              setCredentialToDelete(credential.credential_id);
              toggleDeleteCredentialModal();
            };

            return (
              <div className="grid-span-desktop-6 grid-span-mobile-12" key={index}>
                <CredentialShops
                  country={country}
                  credential={credential}
                  deleteCredential={onDeleteCredential}
                  editCredential={onEditCredential}
                  key={index}
                  selectShop={selectShop}
                  shops={shops.shopsList}
                  toggleAllEstablishments={toggleAllEstablishments}
                  toggleAutoRenewal={onToggleAutomaticRenewal}
                  toggleEstablishment={toggleEstablishment}
                  translateFunction={t}
                />
              </div>
            );
          })}
      </Grid>
      {credentials.credRows && credentials.credRows.length > 0 && (
        <Paginator
          align="right"
          onSelectPage={loadPage}
          pages={credentials && credentials.listSettings.totalPages}
          selected={credentials && credentials.listSettings.currentPage}
        />
      )}
      <CredentialModal
        credentialToEdit={credentialToEdit}
        credentials={credentials}
        showCredentialModal={showCredentialModal}
        toggleCredentialModal={toggleCredentialModal}
      />
      <ConfirmAutoRenewalModal
        onClose={onAutoRenewalModalClose}
        onConfirm={onAutoRenewalModalConfirm}
        visible={autoRenewalModal.isOpen}
      />
      <DeleteCredentialModal
        onDeleteCredential={deleteCredential}
        showDeleteCredentialModal={showDeleteCredentialModal}
        toggleDeleteCredentialModal={toggleDeleteCredentialModal}
      />
    </React.Fragment>
  );
}

export default CredentialsSettings;
